/**
 * Reatom Query Utilities
 * atomWithQuery behavior for Reatom
 */

import { type Atom, atom } from "@reatom/core"
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
} from "@tanstack/react-query"

/**
 * Query state that matches TanStack Query's structure
 */
export interface QueryAtomState<TData = unknown, TError = Error> {
  data: TData | undefined
  isLoading: boolean
  error: TError | undefined
  status: "pending" | "error" | "success"
  isPending: boolean
  isError: boolean
  isSuccess: boolean
}

/**
 * Query options for reatomQuery
 */
export interface ReatomQueryOptions<TData = unknown, TError = Error>
  extends Omit<
    UseQueryOptions<TData, TError>,
    "queryKey" | "queryFn" | "initialData"
  > {
  queryKey: QueryKey
  queryFn: QueryFunction<TData>
}

/**
 * Creates a Reatom atom that holds TanStack Query state
 *
 * @example
 * ```ts
 * const usersQueryAtom = reatomQuery({
 *   queryKey: ['users'],
 *   queryFn: () => fetchUsers(),
 * })
 *
 * // In a reatomComponent:
 * const usersState = usersQueryAtom()
 * if (usersState.isLoading) return <Spinner />
 * if (usersState.isError) return <Error error={usersState.error} />
 * return <UserList users={usersState.data} />
 * ```
 */
export function reatomQuery<TData = unknown, TError = Error>(
  options: ReatomQueryOptions<TData, TError>,
  name?: string,
): Atom<QueryAtomState<TData, TError>> {
  const initialState: QueryAtomState<TData, TError> = {
    data: undefined,
    isLoading: true,
    isPending: true,
    error: undefined,
    status: "pending",
    isError: false,
    isSuccess: false,
  }

  return atom<QueryAtomState<TData, TError>>(
    initialState,
    name || `reatomQuery(${JSON.stringify(options.queryKey)})`,
  )
}

/**
 * Helper to get query params for TanStack Query from a reatomQuery atom
 * Use this with useQuery to fetch data and sync it to the atom
 *
 * @example
 * ```ts
 * const usersQueryAtom = reatomQuery({
 *   queryKey: ['users'],
 *   queryFn: () => fetchUsers(),
 * })
 *
 * // In a data provider component:
 * const query = useQuery(getQueryParams(usersQueryAtom))
 * useEffect(() => {
 *   syncQueryToAtom(usersQueryAtom, query)
 * }, [query])
 * ```
 */
export function getQueryParams<TData, TError>(
  options: ReatomQueryOptions<TData, TError>,
) {
  return options
}

/**
 * Helper to sync TanStack Query result to a reatomQuery atom
 * Call this in useEffect after useQuery
 *
 * @example
 * ```ts
 * const query = useQuery(queryParams)
 * useEffect(() => {
 *   syncQueryToAtom(myQueryAtom, query)
 * }, [query.data, query.isLoading, query.error])
 * ```
 */
export function syncQueryToAtom<TData, TError>(
  queryAtom: Atom<QueryAtomState<TData, TError>>,
  queryResult: {
    data: TData | undefined
    isLoading?: boolean
    isPending?: boolean
    error: TError | null | undefined
    status?: "pending" | "error" | "success"
  },
) {
  const isPending = queryResult.isPending ?? queryResult.isLoading ?? false
  const status =
    queryResult.status ??
    (queryResult.error ? "error" : queryResult.data ? "success" : "pending")

  queryAtom.set({
    data: queryResult.data,
    isLoading: isPending,
    isPending,
    error: queryResult.error ?? undefined,
    status,
    isError: status === "error",
    isSuccess: status === "success",
  })
}
